import { publicJwk } from "../../../../../supabase/functions/sync-control/policy";
import type { LocalStoreClient } from "../localStore/client";
import type { SyncControlClient } from "./controlClient";
import {
  base64url,
  canonical,
  openDeviceEnvelope,
  sealDeviceEnvelope,
  unbase64url,
  unwrapDataKeyBytes,
} from "./cryptoV2";
import type { DeviceVault, UnlockedDevice } from "./deviceVault";
import { RecoveryDraft } from "./recoveryDraft";
import type { SynchronizeV2 } from "./synchronizeV2";
import { dataKeyScope } from "./syncScopes";
import type { VerifyOperation } from "./useSyncReview";
export type ManageMode = "recovery" | "rotate" | "revoke" | "generation" | "rebind";
export type ManagementContext = {
  api: SyncControlClient;
  device: UnlockedDevice;
  vault: DeviceVault;
  client: LocalStoreClient;
  workspaceId: string;
  engine: SynchronizeV2 | null;
};
export async function prepareManagedRecovery(context: ManagementContext, mode: ManageMode, password: string) {
  const root = (await context.api.devices()).root;
  if (!root) throw new Error("sync-root-unavailable");
  if (mode === "generation" || mode === "rebind")
    return RecoveryDraft.prepare(
      { datasetId: crypto.randomUUID(), generation: crypto.randomUUID(), keyVersion: 1 },
      password,
    );
  if (
    context.device.scope.datasetId !== root.datasetId ||
    context.device.scope.generation !== root.generation ||
    context.device.scope.keyVersion !== root.keyVersion
  )
    throw new Error("sync-key-version-required");
  if (mode !== "recovery")
    return RecoveryDraft.prepare(
      { datasetId: root.datasetId, generation: root.generation, keyVersion: root.keyVersion + 1 },
      password,
    );
  const material = await unwrapDataKeyBytes(context.device.wrapping, context.device.scope, context.device.wrapped);
  try {
    return await RecoveryDraft.prepare(
      { datasetId: root.datasetId, generation: root.generation, keyVersion: root.keyVersion },
      password,
      base64url(material),
    );
  } finally {
    material.fill(0);
  }
}
export async function commitManagedRecovery(
  context: ManagementContext,
  mode: ManageMode,
  draft: RecoveryDraft,
  verify: VerifyOperation,
  options: { revokedId?: string; receipt?: string; backupChecksum?: string; confirmedSubject?: string } = {},
) {
  const material = draft.verifiedMaterial();
  const summary = await context.api.devices();
  if (!summary.root) throw new Error("sync-root-unavailable");
  const root = summary.root;
  const { device, api } = context;
  const publicKey = publicJwk(draft.publicKey);
  if (mode === "recovery") {
    if (
      root.datasetId !== material.datasetId ||
      root.generation !== material.generation ||
      root.keyVersion !== material.keyVersion
    )
      throw new Error("sync-key-scope");
    const target = {
      recoveryPublicKey: publicKey,
      datasetId: root.datasetId,
      generation: root.generation,
      keyVersion: root.keyVersion,
    };
    await api.request("recovery-file", {
      recoveryPublicKey: publicKey,
      recoverySignature: await draft.sign({ accountId: device.accountId, ...target }),
      proofId: await verify("recovery-file", target),
    });
    draft.dispose();
    return device;
  }
  const matching =
    root.datasetId === material.datasetId &&
    root.generation === material.generation &&
    root.keyVersion === material.keyVersion &&
    canonical(publicJwk(root.recoveryPublicKey)) === canonical(publicKey);
  if (!matching) {
    const devices =
      mode === "generation"
        ? summary.devices.filter((d) => d.id === device.deviceId && !d.revokedAt)
        : summary.devices.filter((d) => !d.revokedAt && d.id !== options.revokedId);
    const deviceEnvelopes: Record<string, unknown> = {};
    for (const recipient of devices)
      deviceEnvelopes[recipient.id] = await sealDeviceEnvelope(
        recipient.agreement,
        { ...dataKeyScope(material), objectId: recipient.id },
        { dataKey: material.dataKey },
      );
    const nextRoot = {
      datasetId: material.datasetId,
      generation: material.generation,
      keyVersion: material.keyVersion,
      recoveryPublicKey: publicKey,
      deviceEnvelopes,
    };
    if (mode === "generation") {
      if (!options.receipt || !options.backupChecksum) throw new Error("sync-backup-current-required");
      const target = {
        root: nextRoot,
        previousDatasetId: root.datasetId,
        previousGeneration: root.generation,
        backupChecksum: options.backupChecksum,
      };
      await api.request("generation-create", {
        ...target,
        proofId: await verify("generation", target),
        recoverySignature: await draft.sign({ accountId: device.accountId, ...target }),
      });
    } else {
      if (!context.engine) throw new Error("sync-checkpoint-not-ready");
      const key = await crypto.subtle.importKey("raw", unbase64url(material.dataKey, 32), "AES-GCM", false, [
        "encrypt",
        "decrypt",
      ]);
      await context.engine.checkpoint(
        async (checkpoint) => {
          const target = {
            root: { ...nextRoot, checkpoint },
            ...(mode === "revoke"
              ? { revokedDeviceId: options.revokedId, revokedSequence: checkpoint.clock[options.revokedId ?? ""] ?? 0 }
              : {}),
          };
          await api.request(mode, {
            ...target,
            proofId: await verify(mode, target),
            recoverySignature: await draft.sign({ accountId: device.accountId, ...target }),
          });
        },
        true,
        { version: material.keyVersion, key },
      );
    }
  } else {
    const value = (await openDeviceEnvelope(
      device.agreement.privateKey,
      { ...dataKeyScope(root), objectId: device.deviceId },
      root.deviceEnvelopes[device.deviceId] as Awaited<ReturnType<typeof sealDeviceEnvelope>>,
    )) as { dataKey: string };
    if (value.dataKey !== material.dataKey) throw new Error("sync-key-scope");
  }
  const unlocked = await context.vault.create(
    { accountId: device.accountId, deviceId: device.deviceId },
    dataKeyScope(material),
    material.dataKey,
    device.remembered,
    device,
  );
  const scope = {
    datasetId: material.datasetId,
    generation: material.generation,
    keyVersion: material.keyVersion,
    deviceId: device.deviceId,
  };
  if (mode === "generation")
    await context.client.syncGeneration(context.workspaceId, scope, unlocked.dataKey, options.receipt ?? "");
  else {
    const local = await context.client.syncState(context.workspaceId);
    if (local?.keyVersion !== scope.keyVersion)
      await context.client.syncRekey(context.workspaceId, scope, unlocked.dataKey);
  }
  draft.dispose();
  return unlocked;
}
